<template>
  <div class="flex-1 min-h-0 flex flex-col">
    <!-- Loading -->
    <div
      v-if="isLoadingPieces"
      class="flex-1 flex items-center justify-center"
    >
      <div class="w-10 h-10 border-4 border-t-primary border-surface rounded-full animate-spin" />
    </div>

    <!-- Empty -->
    <div
      v-else-if="!pieces?.length"
      class="flex-1 flex flex-col items-center justify-center text-center gap-3"
    >
      <p class="text-text-secondary">
        No musical pieces available. An admin needs to add pieces to the library.
      </p>
    </div>

    <!-- Table -->
    <div
      v-else
      class="flex-1 min-h-0 flex flex-col"
    >
      <PieceFilterBar
        v-model:title="titleFilter"
        v-model:category="categoryFilter"
        v-model:language="languageFilter"
        v-model:placement="placementFilter"
        v-model:genre="genreFilter"
        v-model:stars="starsFilter"
        :genre-options="genreOptions"
        :stars-options="starsOptions"
        class="mb-2"
      />
      <TableSortControlBar
        :active="sorting.length > 0"
        @reset="resetSort"
      />
      <UTable
        ref="tableScroller"
        v-model:sorting="sorting"
        v-model:row-selection="rowSelection"
        :get-row-id="(row) => row._id"
        :data="filteredPieces"
        :columns="pieceColumns"
        sticky
        empty="No pieces match these filters."
        class="h-full"
        :ui="{
          th: 'bg-transparent border-b border-white/10 py-1 px-3',
          td: 'py-3 px-3 text-sm border-b border-white/5 cursor-pointer',
          tr: 'hover:bg-white/[0.03] transition-colors data-[selected=true]:bg-background data-[selected=true]:shadow-[inset_0_0_0_2px_var(--color-surface-hover)]',
          base: 'w-full',
        }"
        @select="onRowSelect"
      >
        <template #name-cell="{ row }">
          <p class="font-medium text-text-primary">
            {{ row.original.name }}
          </p>
        </template>
        <template #category-cell="{ row }">
          <span v-if="row.original.category">{{ PIECE_CATEGORY_LABEL[row.original.category as PieceCategory] }}</span>
          <span
            v-else
            class="text-text-secondary/40"
          >—</span>
        </template>
        <!-- Song and exercise pieces sit at a difficulty, lesson pieces
             in a lesson — one column, whichever the piece actually has. -->
        <template #placement-cell="{ row }">
          <DifficultyBadge
            v-if="row.original.difficulty"
            :name="row.original.difficulty.name"
            :color="row.original.difficulty.color"
          />
          <span v-else-if="row.original.lesson">{{ row.original.lesson.name }}</span>
          <span
            v-else
            class="text-text-secondary/40"
          >—</span>
        </template>
        <template #language-cell="{ row }">
          <span
            v-if="row.original.language"
            class="capitalize"
          >{{ row.original.language }}</span>
          <span
            v-else
            class="text-text-secondary/40"
          >—</span>
        </template>
        <template #tempo-cell="{ row }">
          <span v-if="row.original.tempo">{{ row.original.tempo }}</span>
          <span
            v-else
            class="text-text-secondary/40"
          >—</span>
        </template>
        <template #stars-cell="{ row }">
          <span
            v-if="row.original.category === 'lesson'"
            class="text-text-secondary/40"
          >—</span>
          <div
            v-else-if="(row.original.difficultyStars ?? 0) > 9"
            class="flex items-center gap-1"
          >
            <UIcon
              name="material-symbols-light:kid-star"
              class="text-sm text-gold"
            />
            <span class="text-sm text-gold">×{{ row.original.difficultyStars }}</span>
          </div>
          <div
            v-else
            class="flex items-center gap-0.5"
          >
            <UIcon
              v-for="i in 9"
              :key="i"
              name="material-symbols-light:kid-star"
              :class="i <= (row.original.difficultyStars ?? 0) ? 'text-gold' : 'text-text-secondary/20'"
              class="text-sm"
            />
          </div>
        </template>
        <template #audio-cell="{ row }">
          <UIcon
            :name="row.original.hasAudio ? 'i-lucide-headphones' : 'i-lucide-headphones-off'"
            :class="row.original.hasAudio ? 'text-text-primary' : 'text-text-secondary/30'"
          />
        </template>
      </UTable>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { useConvexQuery } from 'convex-vue';
import { api } from '../../convex/_generated/api';
import type { TableColumn } from '@nuxt/ui';
import type { Id } from '../../convex/_generated/dataModel';
import type { PieceCategory } from '../../convex/types';
import { useTableSorting } from '@/composables/useTableSorting';
import { useSaveScrollPosition } from '@/composables/useSaveScrollPosition';
import { usePieceFilters } from '@/composables/usePieceFilters';
import { piecePlacementSortingFn } from '@/utils/pieceSorting';
import { PIECE_CATEGORY_LABEL } from '@/utils/pieceCategory';
import DifficultyBadge from '@/components/DifficultyBadge.vue';
import PieceFilterBar from '@/components/PieceFilterBar.vue';

// Host-only by construction: `musicalPieces.list` is teacher/admin-gated, so
// the parent must only mount this for the host. A student subscribing gets an
// error result that convex-vue rethrows from `setup` on the next mount.
const props = defineProps<{
  selectedPieceId?: Id<'musicalPieces'> | null;
}>();

const emit = defineEmits<{
  (e: 'select', pieceId: Id<'musicalPieces'>, name: string): void;
}>();

const { data: pieces, isPending: isLoadingPieces } = useConvexQuery(api.musicalPieces.list, {});

const { sorting, getHeader, resetSort } = useTableSorting({
  key: 'classroom-lobby-pieces',
  initialSort: [{ id: 'name', desc: false }],
});
// Filters narrow what the table shows, never what is selected: the selection
// watchers below stay on the full list, so filtering a chosen piece out of
// view leaves the room's pick — and the control bar's name — untouched.
const {
  titleFilter,
  categoryFilter,
  languageFilter,
  placementFilter,
  genreFilter,
  starsFilter,
  filteredPieces,
  genreOptions,
  starsOptions,
} = usePieceFilters(pieces);

const tableScroller = useSaveScrollPosition();
const rowSelection = ref<Record<string, boolean>>({});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const pieceColumns: TableColumn<any>[] = [
  { id: 'name', accessorKey: 'name', header: ({ column }) => getHeader(column, 'Title') },
  { id: 'category', accessorKey: 'category', header: ({ column }) => getHeader(column, 'Category') },
  {
    id: 'placement',
    accessorKey: 'difficulty',
    header: ({ column }) => getHeader(column, 'Difficulty / Lesson'),
    sortingFn: piecePlacementSortingFn,
  },
  { id: 'stars', accessorKey: 'difficultyStars', header: 'Stars', enableSorting: false },
  { id: 'language', accessorKey: 'language', header: ({ column }) => getHeader(column, 'Language') },
  { id: 'tempo', accessorKey: 'tempo', header: ({ column }) => getHeader(column, 'Tempo') },
];

// Row click handler — update selection (song select fires via watch below)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function onRowSelect(_e: Event, row: any) {
  if (row?.original?._id) {
    rowSelection.value = { [row.original._id]: true };
  }
}

// When row selection changes (checkbox or row click), trigger song selection.
// The equality check also swallows the echo from the sync watcher below.
watch(rowSelection, (newVal) => {
  const selectedId = Object.keys(newVal).find(id => newVal[id]);
  if (!selectedId || !pieces.value) return;
  if (props.selectedPieceId === selectedId) return;
  const piece = pieces.value.find((p) => p._id === selectedId);
  if (piece) emit('select', piece._id, piece.name);
});

watch(
  [() => props.selectedPieceId, pieces],
  ([selectedPieceId, list]) => {
    const matched = selectedPieceId && list?.find((piece) => piece._id === selectedPieceId);
    rowSelection.value = matched ? { [matched._id]: true } : {};
  },
  { immediate: true }
);
</script>
